import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useSettings } from "../../contexts/SettingsContext";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  Wrench,
  Package,
  Settings,
  FileText,
  Menu,
  LogOut,
  User,
  Bell,
  ChevronDown,
  Wifi,
  UsersRound,
  Building,
  AlertTriangle,
  ClipboardList,
  FolderKanban,
  CheckSquare,
  Monitor,
  Megaphone,
  FileBarChart,
  Radar,
  Check,
  X,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  SUPERADMIN: "Super Admin",
  GESTION: "Gestión",
  COMERCIAL: "Comercial",
  TECNICO: "Técnico",
  SOPORTE: "Soporte",
};

const roleColors = {
  SUPERADMIN: "bg-purple-600",
  GESTION: "bg-[#03BEE4]",
  COMERCIAL: "bg-[#42E695]",
  TECNICO: "bg-orange-500",
  SOPORTE: "bg-cyan-500",
};

// Map menu items to permission modules
const navItemsConfig = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
  { path: "/coverage-search", label: "Cobertura Fibra", icon: Radar, module: "coverage_search" },
  { path: "/pipeline", label: "Pipeline", icon: TrendingUp, module: "pipeline" },
  { path: "/customers", label: "Clientes", icon: Users, module: "customers" },
  { path: "/provisions", label: "Provisión", icon: Package, module: "provisions" },
  { path: "/incidents", label: "Incidencias", icon: AlertTriangle, module: "incidents" },
  { path: "/requests", label: "Solicitudes", icon: ClipboardList, module: "requests" },
  { path: "/it-support", label: "Tickets Internos", icon: Monitor, module: "it_tickets" },
  { path: "/catalog", label: "Catálogo", icon: Package, module: "catalog" },
  { path: "/providers", label: "Proveedores", icon: Building, module: "providers" },
  { path: "/announcements", label: "Anuncios", icon: Megaphone, module: "announcements" },
  { path: "/reports", label: "Informes", icon: FileBarChart, module: "reports" },
  { path: "/users", label: "Usuarios", icon: UsersRound, module: "users" },
  { path: "/audit-log", label: "Auditoría", icon: FileText, module: "audit" },
  { path: "/checklist-master", label: "Checklist Maestro", icon: CheckSquare, module: "checklist_master" },
  { path: "/settings", label: "Configuración", icon: Settings, module: "settings" },
];

const Sidebar = ({ isMobile = false, onClose }) => {
  const { user, switchRole, canView } = useAuth();
  const { settings } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Filter nav items based on permissions
  const navItems = navItemsConfig.filter(item => canView(item.module));

  const handleRoleSwitch = async (newRole) => {
    const result = await switchRole(newRole);
    if (result.success) {
      toast.success(`Rol cambiado a ${roleLabels[newRole]}`);
      navigate("/dashboard");
      if (onClose) onClose();
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#04042C] border-r border-[#1E1E5F]">
      {/* Logo */}
      <div className="p-6 border-b border-[#1E1E5F]">
        <div className="flex items-center gap-3">
          <img 
            src="/wilan-logo.png" 
            alt={settings.platform_name}
            className="w-10 h-10 object-contain"
          />
          <div>
            <h1 className="text-xl font-heading font-semibold text-white">{settings.platform_name}</h1>
            <p className="text-xs text-slate-400">{settings.platform_subtitle}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.path.replace("/", "")}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
                  isActive
                    ? "bg-[#42E695]/10 text-[#42E695] border border-[#42E695]/30"
                    : "text-slate-300 hover:bg-[#1B1B4B] hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Role Switcher - only for SUPERADMIN/GESTION */}
      {user?.roles?.some(r => r === 'SUPERADMIN' || r === 'GESTION') && (
        <div className="p-4 border-t border-[#1E1E5F]">
          <label className="text-xs text-slate-400 mb-2 block">Cambiar Rol</label>
          <Select value={user.active_role} onValueChange={handleRoleSwitch}>
            <SelectTrigger 
              data-testid="role-switcher-select"
              className="w-full bg-[#0A0A35] border-[#1E1E5F] text-white"
            >
              <SelectValue>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${roleColors[user.active_role]}`}></span>
                  {roleLabels[user.active_role]}
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
              {user.roles.map((role) => (
                <SelectItem 
                  key={role} 
                  value={role}
                  className="text-white hover:bg-[#1B1B4B]"
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${roleColors[role]}`}></span>
                    {roleLabels[role]}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications`),
        axios.get(`${API_URL}/api/notifications/unread-count`),
      ]);
      setNotifications(notifRes.data.slice(0, 10)); // Show max 10 in dropdown
      setUnreadCount(countRes.data.unread_count);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 10 seconds for real-time feel
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    // Mark as read automatically
    if (!notification.is_read) {
      try {
        await axios.put(`${API_URL}/api/notifications/${notification.id}/read`);
        fetchNotifications();
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }
    // Navigate to the entity if link exists
    if (notification.link) {
      navigate(notification.link);
      setNotificationsOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`);
      fetchNotifications();
      toast.success("Todas las notificaciones marcadas como leídas");
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Sesión cerrada");
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "US";
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Ahora";
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString("es-ES");
  };

  return (
    <header className="h-16 border-b border-[#1E1E5F] bg-[#04042C]/80 backdrop-blur-lg sticky top-0 z-40">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Mobile Menu Button */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu className="h-6 w-6 text-white" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 bg-[#04042C] border-[#1E1E5F]">
            <Sidebar isMobile onClose={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* Search/Title area */}
        <div className="flex-1 lg:pl-0">
          {user?.roles?.some(r => r === 'SUPERADMIN' || r === 'GESTION') ? (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${roleColors[user?.active_role]} text-white`}>
              {roleLabels[user?.active_role]}
            </span>
          ) : (
            <div className="flex items-center gap-1">
              {user?.roles?.map(r => (
                <span key={r} className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${roleColors[r] || 'bg-slate-600'} text-white`}>
                  {roleLabels[r] || r}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* Notifications */}
          <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="relative text-slate-300 hover:text-white"
                data-testid="notifications-btn"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#42E695] rounded-full flex items-center justify-center text-[10px] font-bold text-[#04042C]">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent 
              align="end" 
              className="w-80 p-0 bg-[#0A0A35] border-[#1E1E5F]"
            >
              <div className="flex items-center justify-between p-3 border-b border-[#1E1E5F]">
                <h4 className="text-white font-medium">Notificaciones</h4>
                {unreadCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleMarkAllRead}
                    className="text-xs text-[#42E695] hover:text-[#42E695]/80 h-7"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Marcar todas leídas
                  </Button>
                )}
              </div>
              <div
                className="max-h-96 overflow-y-auto overscroll-contain"
                data-testid="notifications-list"
              >
                {notifications.length === 0 ? (
                  <div className="p-6 text-center">
                    <Bell className="h-8 w-8 text-slate-500 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm">No hay notificaciones</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#1E1E5F]">
                    {notifications.map((notif) => (
                      <div
                        key={notif.id}
                        onClick={() => handleNotificationClick(notif)}
                        className={`p-3 cursor-pointer transition-colors hover:bg-[#1B1B4B]/50 ${
                          !notif.is_read ? "bg-[#1B1B4B]/30" : ""
                        }`}
                        data-testid={`notification-${notif.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${
                            !notif.is_read ? "bg-[#42E695]" : "bg-transparent"
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${!notif.is_read ? "text-white font-medium" : "text-slate-300"}`}>
                              {notif.title}
                            </p>
                            <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                              {notif.message}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {formatTimeAgo(notif.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="flex items-center gap-2 text-white hover:bg-[#1B1B4B]"
                data-testid="user-menu-btn"
              >
                <Avatar className="h-8 w-8 bg-[#1B1B4B] border border-[#1E1E5F]">
                  <AvatarFallback className="bg-[#42E695] text-[#04042C] text-sm font-semibold">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:inline text-sm">{user?.name}</span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#0A0A35] border-[#1E1E5F]">
              <DropdownMenuItem 
                onClick={() => navigate("/profile")}
                className="text-white hover:bg-[#1B1B4B] cursor-pointer"
              >
                <User className="mr-2 h-4 w-4" />
                Mi Perfil
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#1E1E5F]" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 hover:bg-red-900/30 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

const DashboardLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#04042C] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 fixed inset-y-0 left-0 z-50">
        <Sidebar />
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        <Header />
        <main className="flex-1 p-4 lg:p-6 overflow-auto animate-fade-in-up">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
